/**
 * The service retirement map (Estimate Tool 8.8, open question 11; Phase E9).
 *
 * The five website templates were cut in 2019 and still assign hours to six services the account
 * has since deactivated — between 27 % and 37 % of every template's hours, and 359.5 of the
 * Medium template's 979. Workamajig will accept a write against a deactivated service without
 * complaint (the E8 spike wrote one and read it back), so this is a decision the generator has to
 * make rather than an error it will be told about.
 *
 * **Every mapping here is inferred, not confirmed.** It is open question 11 and it is on the E9
 * review gate. The evidence is the account's own website projects: what they booked on each code
 * before it was deactivated, and what they booked instead afterwards. The one with real weight is
 * `PM`, which carries 160 of the Medium template's hours; the other five have been unused since
 * well before 2024 and their successors are a matter of reading the descriptions.
 *
 * This is the only place in the codebase that names a retired service. It is a constant rather
 * than a setting for the same reason `POOL_TYPES` is (Estimate Tool 12.4): re-homing a third of
 * every schedule's hours is a decision, and a decision that can be changed from a settings screen
 * is a decision nobody has to take again.
 */

/**
 * Deactivated service code to the live one its work goes to now.
 *
 * `hours` is what the Medium template assigns to that code, and `evidence` is why the successor
 * is the successor — both carried here so the plan can explain a re-homed line on the screen and
 * so a reviewer can argue with the map without leaving the file.
 */
export const SERVICE_RETIREMENTS = Object.freeze({
  PM: Object.freeze({
    to: 'ACM',
    from_name: 'Project Management',
    to_name: 'Account Management',
    medium_template_hours: 160,
    deactivated_on: '2025-09-30',
    evidence:
      'Website hours moved to ACM across the switch-off: 2,712.9 h on PM and 651.9 h on ACM in the twenty months before, 57.3 h and 907.0 h in the eleven months since. PRC Program Coordination took only 72.5 h.',
  }),
  CS: Object.freeze({
    to: 'CSW',
    from_name: 'Content Strategy',
    to_name: 'Content Strategy Web',
    medium_template_hours: 107.5,
    deactivated_on: '2023-01-11',
    evidence:
      'CSW is the live content-strategy code on websites and carries the work CS used to: 514.8 h since October 2025, against nothing on CS since before 2024.',
  }),
  AM: Object.freeze({
    to: 'AS',
    from_name: 'Analytics/Metrics',
    to_name: 'Analytics Setup',
    medium_template_hours: 40,
    deactivated_on: '2023-01-11',
    evidence:
      'The template uses AM for analytics set-up at launch rather than for reporting, which is AS. AR Analytics Reporting is the alternative and is the one to argue for if the template line means the ongoing report.',
  }),
  SEI: Object.freeze({
    to: 'SEO',
    from_name: 'SEO/SEM Implementation',
    to_name: 'SEO Implementation',
    medium_template_hours: 28,
    deactivated_on: '2023-01-11',
    evidence: 'Same work, same description, renamed. SEO carries 309.8 h since October 2025.',
  }),
  SEP: Object.freeze({
    to: 'DMS',
    from_name: 'SEO/SEM Strategy Planning',
    to_name: 'Digital Marketing Strategy',
    medium_template_hours: 16,
    deactivated_on: '2023-01-11',
    evidence:
      'The planning half of the old SEO/SEM trio; DMS is where digital strategy planning is booked now.',
  }),
  SER: Object.freeze({
    to: 'DMR',
    from_name: 'SEO/SEM Research',
    to_name: 'Digital Marketing Research',
    medium_template_hours: 8,
    deactivated_on: '2023-01-11',
    evidence: 'The research half of the same trio; DMR is its live counterpart.',
  }),
});

/** Every code this map retires, for a screen that wants to say what it looked for. */
export const RETIRED_CODES = Object.freeze(Object.keys(SERVICE_RETIREMENTS));

export class UnmappedServiceError extends Error {
  /** @param {string} code @param {string} [where] */
  constructor(code, where) {
    super(
      `service ${code} is not active in Workamajig and has no entry in the retirement map` +
        (where ? ` (${where})` : '') +
        '. Add it to SERVICE_RETIREMENTS in lib/estimate/services.js, or reactivate the service.',
    );
    this.name = 'UnmappedServiceError';
    this.code = 'UNMAPPED_SERVICE';
    this.serviceCode = code;
  }
}

/**
 * Re-home one service code.
 *
 * This runs **before calibration and before scaling** (Estimate Tool 8.4), so that history is
 * matched and hours are scaled against the code the work will actually be booked to, and both
 * codes are carried on the line: a service that silently changes between the template and the
 * schedule is the kind of thing nobody notices until an invoice is wrong.
 *
 * A code that is neither active nor mapped **raises**. Passing it through would write a
 * deactivated service into a real project, and guessing a successor is exactly the decision this
 * map exists to have taken deliberately.
 *
 * @param {string} code                 the template's service code
 * @param {Set<string>|null} activeCodes the account's active services; null skips the check,
 *                                       for a caller that has no service list to hand
 * @param {{ where?: string }} [opts]
 * @returns {{ code: string, retired_from: string|null, retirement: object|null }}
 */
export function rehomeService(code, activeCodes, { where } = {}) {
  const raw = String(code ?? '').trim();
  if (!raw) throw new UnmappedServiceError('(blank)', where);
  if (activeCodes && activeCodes.has(raw)) {
    return { code: raw, retired_from: null, retirement: null };
  }
  const retirement = SERVICE_RETIREMENTS[raw];
  if (retirement) {
    // A map that pointed at a code the account has since retired too would re-home a line onto a
    // second dead service and report it as fixed.
    if (activeCodes && !activeCodes.has(retirement.to)) {
      throw new UnmappedServiceError(
        `${raw} (mapped to ${retirement.to}, which is itself not active)`,
        where,
      );
    }
    return { code: retirement.to, retired_from: raw, retirement };
  }
  // With no service list to check against, an unretired code is taken at its word: the caller
  // could not have told us it was dead.
  if (!activeCodes) return { code: raw, retired_from: null, retirement: null };
  throw new UnmappedServiceError(raw, where);
}
